import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { BackgroundBlobs } from '@/components/BackgroundBlobs';
import { AlertTriangle, ExternalLink, Loader2, Search, Gift, ShoppingBag, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/Button';

interface LinkRedirectProps {
    shortCode: string;
}

export const LinkRedirect: React.FC<LinkRedirectProps> = ({ shortCode }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [countdown, setCountdown] = useState(30);
    const [targetUrl, setTargetUrl] = useState<string | null>(null);
    const [showBenefits, setShowBenefits] = useState(false);
    const hasLoggedClick = React.useRef(false);
    const fetchedRef = React.useRef(false);

    useEffect(() => {
        const checkRedirect = async () => {
            if (fetchedRef.current) return;
            fetchedRef.current = true;

            try {
                // Fetch the link using RPC to bypass RLS
                const { data, error: linkError } = await supabase
                    .rpc('resolve_affiliate_link', { code: shortCode })
                    .single();

                const linkData = data as any;

                if (linkError || !linkData) {
                    setError('O link que você tentou acessar não foi encontrado ou expirou.');
                    setLoading(false);
                    return;
                }

                setTargetUrl(linkData.short_url);

                // Check session
                const { data: { session } } = await supabase.auth.getSession();

                if (session?.user) {
                    const params = new URLSearchParams(window.location.search);

                    let finalTargetUrl = linkData.short_url;
                    let finalAffiliateLinkId = linkData.affiliate_link_id;

                    try {
                        if (linkData.provider === 'mercadolivre' || linkData.provider === 'shopee') {
                            const { data: integration } = await supabase
                                .from('integrations')
                                .select('platform_account, status')
                                .eq('provider', linkData.provider)
                                .single();

                            const userPlatformAccount = (integration?.status === 'activated' && integration?.platform_account) ? integration.platform_account : 'main';
                            const linkPlatformAccount = linkData.platform_account || 'main';

                            if (userPlatformAccount !== linkPlatformAccount) {
                                console.log(`Mismatch integration detected. Link: ${linkPlatformAccount}, User: ${userPlatformAccount}. Regenerating...`);

                                const generateRes = await fetch('/.netlify/functions/generate-link', {
                                    method: 'POST',
                                    headers: {
                                        'Content-Type': 'application/json',
                                        'Authorization': `Bearer ${session.access_token}`
                                    },
                                    body: JSON.stringify({
                                        url: linkData.original_url,
                                        platform: linkData.provider,
                                    })
                                });

                                if (generateRes.ok) {
                                    const genData = await generateRes.json();
                                    finalTargetUrl = genData.platform_url || genData.short_url || finalTargetUrl;
                                    finalAffiliateLinkId = genData.id || finalAffiliateLinkId;
                                } else {
                                    console.warn("Failed to generate new link on the fly, falling back to original");
                                }
                            }
                        }
                    } catch (e) {
                        console.error("Error validating or regenerating link", e);
                    }

                    // Log the click only once (prevent StrictMode double inserts)
                    if (!hasLoggedClick.current) {
                        hasLoggedClick.current = true;

                        let userIp = null;
                        try {
                            const ipResponse = await fetch('https://api.ipify.org?format=json');
                            if (ipResponse.ok) {
                                const ipData = await ipResponse.json();
                                userIp = ipData.ip;
                            }
                        } catch (e) {
                            console.warn("Could not fetch IP", e);
                        }

                        await supabase
                            .from('affiliate_link_clicks')
                            .insert({
                                user_id: session.user.id,
                                affiliate_link_id: finalAffiliateLinkId,
                                redirect_url: finalTargetUrl,
                                ip_address: userIp,
                                user_agent: window.navigator.userAgent,
                                referrer: document.referrer || null,
                                utm_source: params.get('utm_source'),
                                utm_medium: params.get('utm_medium'),
                                utm_campaign: params.get('utm_campaign'),
                            });
                    }

                    // Redirect immediately
                    window.location.href = finalTargetUrl;
                } else {
                    // Show warning and countdown
                    setLoading(false);
                }
            } catch (err) {
                console.error(err);
                setError('Ocorreu um erro ao carregar o link.');
                setLoading(false);
            }
        };

        checkRedirect();
    }, [shortCode]);

    useEffect(() => {
        if (!loading && !error && targetUrl && countdown > 0) {
            const timer = setTimeout(() => {
                setCountdown((prev) => prev - 1);
            }, 1000);
            return () => clearTimeout(timer);
        } else if (countdown === 0 && targetUrl) {
            window.location.href = targetUrl;
        }
    }, [countdown, loading, error, targetUrl]);

    if (loading) {
        return (
            <div className="relative min-h-screen flex items-center justify-center p-4 bg-gray-50 dark:bg-[#0a0a0c]">
                <BackgroundBlobs />
                <div className="z-10 flex flex-col items-center gap-4 animate-pulse text-gray-400">
                    <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
                    <p className="font-medium">Redirecionando...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="relative min-h-screen flex items-center justify-center p-4 bg-gray-50 dark:bg-[#0a0a0c]">
                <BackgroundBlobs />
                <div className="z-10 bg-white dark:bg-gray-900 rounded-3xl p-8 max-w-sm w-full text-center shadow-xl border border-white/20 dark:border-gray-800">
                    <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                        <AlertTriangle className="w-8 h-8" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Ops! Alguma coisa deu errado.</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{error}</p>
                    <a href="/">
                        <Button className="w-full">Voltar para Início</Button>
                    </a>
                </div>
            </div>
        );
    }

    return (
        <div className="relative min-h-screen flex items-center justify-center p-4 bg-gray-50 dark:bg-[#0a0a0c]">
            <BackgroundBlobs />
            <div className="z-10 bg-white dark:bg-gray-900 rounded-3xl overflow-hidden max-w-5xl w-full shadow-2xl border border-white/20 dark:border-gray-800 animate-in zoom-in-95 flex flex-col md:flex-row">

                {/* Left side: Pitch / Explication */}
                <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 p-6 md:p-12 md:w-1/2 flex flex-col justify-center text-left text-white relative overflow-hidden">
                    {/* Decorative background elements */}
                    <div className="absolute top-0 right-0 -mt-16 -mr-16 w-64 h-64 bg-white opacity-5 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-0 left-0 -mb-16 -ml-16 w-48 h-48 bg-indigo-400 opacity-20 rounded-full blur-2xl"></div>

                    <div className="relative z-10">
                        {/* Logo and Name */}
                        <div className="flex items-center gap-3 mb-4 md:mb-8">
                            <div className="w-10 h-10 md:w-12 md:h-12 bg-white rounded-full p-1.5 shadow-lg shadow-black/10 flex shrink-0 items-center justify-center">
                                <img src="/logo.png" alt="Radar da Economia" className="w-full h-full object-contain" />
                            </div>
                            <span className="text-xl md:text-2xl font-black tracking-tight text-white">Radar da Economia</span>
                        </div>

                        <h2 className="text-xl md:text-4xl font-black mb-3 md:mb-4 leading-tight">
                            Compre com desconto e ainda <span className="text-yellow-300">ganhe recompensas</span>
                        </h2>

                        <p className="text-blue-100 mb-4 md:mb-8 font-medium text-sm md:text-lg leading-relaxed">
                            O Radar da Economia encontra as melhores ofertas da internet para você economizar. Se você criar uma conta, cada compra vira pontos!
                        </p>

                        {/* Collapsible section on mobile */}
                        <div className="md:block">
                            <button
                                onClick={() => setShowBenefits(!showBenefits)}
                                className="w-full flex items-center justify-between md:hidden bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl p-3 mb-4 text-sm font-bold transition-colors shadow-sm"
                            >
                                Como funciona?
                                {showBenefits ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                            </button>

                            <ul className={`space-y-4 md:space-y-6 ${showBenefits ? 'block' : 'hidden md:block'}`}>
                                <li className="flex items-start gap-3 md:gap-4">
                                    <div className="bg-white/10 p-2 md:p-3 rounded-xl shrink-0 backdrop-blur-sm border border-white/10">
                                        <Search className="w-5 h-5 md:w-6 md:h-6 text-blue-200" />
                                    </div>
                                    <div>
                                        <strong className="block text-base md:text-xl font-bold mb-0.5 md:mb-1">Ofertas Verificadas</strong>
                                        <span className="text-blue-100/90 text-xs md:text-sm leading-snug md:leading-relaxed block">Monitoramos as grandes lojas para trazer descontos reais todos os dias.</span>
                                    </div>
                                </li>
                                <li className="flex items-start gap-3 md:gap-4">
                                    <div className="bg-white/10 p-2 md:p-3 rounded-xl shrink-0 backdrop-blur-sm border border-white/10">
                                        <Gift className="w-5 h-5 md:w-6 md:h-6 text-yellow-300" />
                                    </div>
                                    <div>
                                        <strong className="block text-base md:text-xl font-bold mb-0.5 md:mb-1 text-yellow-300">Acumule Pontos</strong>
                                        <span className="text-blue-100/90 text-xs md:text-sm leading-snug md:leading-relaxed block">Comprando logado pelos nossos links, você pode ganhar pontos na plataforma.</span>
                                    </div>
                                </li>
                                <li className="flex items-start gap-3 md:gap-4">
                                    <div className="bg-white/10 p-2 md:p-3 rounded-xl shrink-0 backdrop-blur-sm border border-white/10">
                                        <ShoppingBag className="w-5 h-5 md:w-6 md:h-6 text-green-300" />
                                    </div>
                                    <div>
                                        <strong className="block text-base md:text-xl font-bold mb-0.5 md:mb-1 text-green-300">Troque por Prêmios</strong>
                                        <span className="text-blue-100/90 text-xs md:text-sm leading-snug md:leading-relaxed block">Resgate seus pontos por produtos, vales-presente e prêmios exclusivos.</span>
                                    </div>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Right side: Redirect action */}
                <div className="p-6 md:p-12 md:w-1/2 flex flex-col justify-center items-center text-center bg-white dark:bg-gray-900 border-t md:border-t-0 md:border-l border-gray-100 dark:border-gray-800">
                    <div className="hidden md:flex w-16 h-16 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400 rounded-2xl items-center justify-center mx-auto mb-6 rotate-3">
                        <AlertTriangle className="w-8 h-8" />
                    </div>

                    <h3 className="hidden md:block text-2xl font-black text-gray-900 dark:text-white mb-2">Você não está logado!</h3>

                    <div className="bg-red-50 dark:bg-red-900/10 rounded-2xl p-3 md:p-4 mb-2 md:mb-4 border border-red-100 dark:border-red-900/30 w-full text-left md:text-center">
                        <p className="text-xs md:text-sm text-gray-700 dark:text-gray-300 font-medium">
                            Continuar agora significa que <span className="text-red-600 dark:text-red-400 font-bold">você não ganhará pontos</span> por esta compra.
                        </p>
                    </div>

                    <div className="mb-2 md:mb-4 relative w-full flex justify-center">
                        {/* Countdown circle */}
                        <div className="relative flex items-center justify-center w-24 h-24 md:w-32 md:h-32">
                            <svg viewBox="0 0 128 128" className="absolute inset-0 w-full h-full transform -rotate-90">
                                <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="8" fill="transparent" className="text-gray-100 dark:text-gray-800" />
                                <circle cx="64" cy="64" r="56" stroke="currentColor" strokeWidth="8" fill="transparent"
                                    strokeDasharray="351.8"
                                    strokeDashoffset={351.8 - (351.8 * countdown) / 30}
                                    strokeLinecap="round"
                                    className="text-blue-500 transition-all duration-1000 ease-linear" />
                            </svg>
                            <div className="flex flex-col items-center justify-center z-10 pt-1">
                                <div className="text-3xl md:text-5xl font-black text-gray-900 dark:text-white font-mono leading-none">
                                    {countdown}
                                </div>
                                <span className="text-[9px] md:text-[10px] text-gray-400 font-bold uppercase tracking-wider mt-1">Segundos</span>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col gap-2 md:gap-3 w-full mt-1">
                        <a href={`/?login=true&returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`} className="w-full">
                            <Button variant="primary" className="w-full text-sm md:text-base py-4 md:py-6 shadow-lg shadow-blue-500/25">
                                Fazer Login ou Criar Conta
                            </Button>
                        </a>

                        <button
                            onClick={() => {
                                if (targetUrl) window.location.href = targetUrl;
                            }}
                            className="w-full flex items-center justify-center gap-2 px-4 md:px-6 py-3 md:py-4 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-gray-300 text-gray-700 text-sm md:text-base font-bold rounded-xl transition-colors"
                        >
                            Continuar sem ganhar pontos <ExternalLink className="w-4 h-4 opacity-50" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
